import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { 
  Car, LayoutDashboard, BarChart3, CreditCard, LogOut, Loader2,
  DollarSign, Plus, Receipt, Check, Clock, AlertCircle, ExternalLink
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from "../../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdvertiserBilling() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [advertiser, setAdvertiser] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentProcessing, setPaymentProcessing] = useState(false);

  useEffect(() => {
    // Check for payment return
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
    checkAuth();
  }, []);

  useEffect(() => {
    if (advertiser) fetchInvoices();
  }, [advertiser]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/advertiser-portal/me`, { withCredentials: true });
      setAdvertiser(response.data);
    } catch (error) {
      navigate("/advertiser/login");
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/advertiser-portal/invoices`, { withCredentials: true });
      setInvoices(response.data);
    } catch (error) {
      toast.error("Failed to load invoices");
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/advertiser-portal/payment-status/${sessionId}`, { withCredentials: true });
      if (response.data.payment_status === "paid") {
        toast.success("Payment successful! Your account has been credited.");
        // Refresh advertiser data to show new balance
        const advResponse = await axios.get(`${API}/advertiser-portal/me`, { withCredentials: true });
        setAdvertiser(advResponse.data);
      }
    } catch (error) {
      console.error("Payment status check failed:", error);
    }
  };

  const handlePayment = async (amount, invoiceId = null) => {
    setPaymentProcessing(true);
    try {
      const response = await axios.post(`${API}/advertiser-portal/pay`, {
        amount: parseFloat(amount),
        invoice_id: invoiceId,
        description: invoiceId ? `Invoice payment` : "Account credit",
        origin_url: window.location.origin
      }, { withCredentials: true });
      
      // Redirect to Stripe
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create payment");
      setPaymentProcessing(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/advertiser-portal/logout`, {}, { withCredentials: true });
    } catch {}
    navigate("/advertiser/login");
  };

  const statusColors = {
    draft: "bg-slate-500/20 text-slate-400",
    sent: "bg-blue-500/20 text-blue-400",
    paid: "bg-emerald-500/20 text-emerald-400",
    overdue: "bg-red-500/20 text-red-400"
  };

  const totalOutstanding = invoices
    .filter(i => i.status === "sent" || i.status === "overdue")
    .reduce((sum, i) => sum + (i.total || 0), 0);

  return (
    <div className="min-h-screen bg-slate-950" data-testid="advertiser-billing">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-600 to-green-500 flex items-center justify-center">
                <Car className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  ParkWise
                </span>
                <span className="hidden sm:inline text-slate-400 text-sm ml-2">Advertiser Portal</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-400 hidden sm:block">
                {advertiser?.company_name}
              </span>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-400">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-slate-900/50 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto py-2">
            <Link to="/advertiser" className="px-4 py-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white text-sm font-medium whitespace-nowrap">
              <LayoutDashboard className="w-4 h-4 inline mr-2" />Dashboard
            </Link>
            <Link to="/advertiser/campaigns" className="px-4 py-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white text-sm font-medium whitespace-nowrap">
              <BarChart3 className="w-4 h-4 inline mr-2" />Campaigns
            </Link>
            <Link to="/advertiser/billing" className="px-4 py-2 rounded-lg bg-slate-800 text-white text-sm font-medium whitespace-nowrap">
              <CreditCard className="w-4 h-4 inline mr-2" />Billing
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Billing & Payments
            </h1>
            <p className="text-slate-400 text-sm mt-1">Manage your invoices and account balance</p>
          </div>
          <Dialog open={isPaymentOpen} onOpenChange={setIsPaymentOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-funds-btn">
                <Plus className="w-4 h-4 mr-2" />Add Funds
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-700 text-white">
              <DialogHeader>
                <DialogTitle>Add Funds to Account</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div>
                  <Label className="text-slate-300">Amount (£)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="10"
                    placeholder="100.00"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    className="bg-slate-800 border-slate-700 text-white mt-2"
                    data-testid="payment-amount"
                  />
                  <p className="text-xs text-slate-500 mt-1">Minimum £10.00</p>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[50, 100, 500].map(amount => (
                    <Button
                      key={amount}
                      variant="outline"
                      className="border-slate-700 text-slate-300"
                      onClick={() => setPaymentAmount(String(amount))}
                    >
                      £{amount}
                    </Button>
                  ))}
                </div>
                <Button
                  onClick={() => handlePayment(paymentAmount)}
                  disabled={!paymentAmount || parseFloat(paymentAmount) < 10 || paymentProcessing}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="confirm-payment"
                >
                  {paymentProcessing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CreditCard className="w-4 h-4 mr-2" />
                  )}
                  Pay with Card
                </Button>
                <p className="text-xs text-slate-500 text-center">
                  Secure payment powered by Stripe
                </p>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Balance Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-emerald-600/20 to-emerald-600/5 border-emerald-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-emerald-300">Account Balance</p>
                <DollarSign className="w-5 h-5 text-emerald-400" />
              </div>
              <p className="text-3xl font-bold text-white">
                £{(advertiser?.account_balance || 0).toFixed(2)}
              </p>
              <p className="text-xs text-emerald-300 mt-2">Available for campaigns</p>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-slate-400">Outstanding</p>
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
              <p className="text-3xl font-bold text-white">
                £{totalOutstanding.toFixed(2)}
              </p>
              <p className="text-xs text-slate-500 mt-2">
                {invoices.filter(i => i.status === "sent" || i.status === "overdue").length} unpaid invoices
              </p>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-slate-400">Credit Limit</p>
                <Receipt className="w-5 h-5 text-blue-400" />
              </div>
              <p className="text-3xl font-bold text-white">
                £{(advertiser?.credit_limit || 0).toFixed(2)}
              </p>
              <p className="text-xs text-slate-500 mt-2">For invoice billing</p>
            </CardContent>
          </Card>
        </div>

        {/* Invoices */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Invoices</CardTitle>
            <CardDescription>Your billing history and outstanding payments</CardDescription>
          </CardHeader>
          <CardContent>
            {invoices.length > 0 ? (
              <div className="space-y-4">
                {invoices.map((invoice) => (
                  <div 
                    key={invoice.invoice_id} 
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-slate-900/50 rounded-lg gap-4"
                  >
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-white">{invoice.invoice_number}</p>
                        <Badge className={statusColors[invoice.status]}>{invoice.status}</Badge>
                      </div>
                      <p className="text-sm text-slate-400">
                        {invoice.period_start} - {invoice.period_end}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-lg font-bold text-white">£{(invoice.total || 0).toFixed(2)}</p>
                        <p className="text-xs text-slate-500">Due: {invoice.due_date}</p>
                      </div>
                      {(invoice.status === "sent" || invoice.status === "overdue") && (
                        <Button 
                          size="sm" 
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => handlePayment(invoice.total, invoice.invoice_id)}
                          disabled={paymentProcessing}
                        >
                          {paymentProcessing ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>Pay Now</>
                          )}
                        </Button>
                      )}
                      {invoice.status === "paid" && (
                        <Check className="w-5 h-5 text-emerald-400" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Receipt className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No invoices yet</p>
                <p className="text-sm text-slate-500 mt-1">Invoices will appear here when generated</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payment Methods Info */}
        <div className="mt-8 p-4 bg-slate-800/30 border border-slate-700 rounded-lg">
          <h3 className="text-sm font-medium text-slate-300 mb-2">Payment Information</h3>
          <p className="text-sm text-slate-400">
            We accept all major credit and debit cards through our secure payment partner Stripe. 
            Funds are credited instantly to your account balance and can be used for campaigns.
          </p>
        </div>
      </main>
    </div>
  );
}
